import { ModelProvider } from "@/services/promptEditor.service";
import dayjs from "dayjs";
import _ from "lodash";
import React, { createContext, useCallback, useContext, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";

export type LocalStorageKey = { key: string; timestamp: number };
export type Keys = Partial<Record<ModelProvider, LocalStorageKey>>;

interface ProviderKeysContextInterface {
  keys: Keys;
  setKey: (provider: ModelProvider, key: string) => void;
  deleteKey: (provider: ModelProvider) => void;
}

const ProviderKeysContext = createContext<ProviderKeysContextInterface | undefined>(undefined);

/**
 * @returns The model provider keys stored locally.
 */
export const useLocalProviderKeys = () => {
  const context = useContext(ProviderKeysContext);
  if (context === undefined) {
    throw new Error("useProviderKeys must be used within a ProviderKeysContextProvider");
  }
  return context;
};

export const ProviderKeysContextProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const [keys, setKeys] = useLocalStorage<Keys>(`provider-keys`, {});

  const setKey = useCallback(
    (provider: ModelProvider, key: string) => {
      setKeys({ ...keys, [provider]: { key, timestamp: dayjs().unix() } });
    },
    [keys, setKeys],
  );

  const deleteKey = useCallback(
    (provider: ModelProvider) => {
      setKeys(_.omit(keys, provider));
    },
    [keys, setKeys],
  );

  return <ProviderKeysContext.Provider value={{ keys, setKey, deleteKey }}>{children}</ProviderKeysContext.Provider>;
};

/**
 * Converts keys (as stored in local storage, with timestamp) to provider keys as required by the API.
 * @param keys
 * @returns
 */
export const keysToRequestInterface = (keys: Keys): Record<ModelProvider, string> => {
  return Object.fromEntries(Object.entries(keys).map(([provider, { key }]) => [provider.toLowerCase(), key])) as Record<
    ModelProvider,
    string
  >;
};
